"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "../sonner";

export type LiveSavingPhase = "idle" | "processing" | "success";

/** Default duration the success tick stays visible after an async save resolves. */
export const LIVE_SAVING_SUCCESS_TICK_DURATION_MS = 2_000;

const DEFAULT_SUCCESS_TICK_DURATION_MS = LIVE_SAVING_SUCCESS_TICK_DURATION_MS;

export interface UseLiveSavingPhaseOptions {
	successTickDurationMs?: number;
	onError?: () => void;
}

export function useLiveSavingPhase({
	successTickDurationMs = DEFAULT_SUCCESS_TICK_DURATION_MS,
	onError,
}: UseLiveSavingPhaseOptions = {}) {
	const [phase, setPhase] = useState<LiveSavingPhase>("idle");
	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	const clearSuccessTimeout = useCallback(() => {
		if (successTimeoutRef.current != null) {
			clearTimeout(successTimeoutRef.current);
			successTimeoutRef.current = null;
		}
	}, []);

	useEffect(() => clearSuccessTimeout, [clearSuccessTimeout]);

	const runLiveSaving = useCallback(
		async (action: () => Promise<void>) => {
			clearSuccessTimeout();
			setPhase("processing");
			try {
				await action();
				setPhase("success");
				successTimeoutRef.current = setTimeout(() => {
					setPhase("idle");
					successTimeoutRef.current = null;
				}, successTickDurationMs);
			} catch {
				toast.error("Could not save setting. Please try again.");
				onError?.();
				setPhase("idle");
			}
		},
		[clearSuccessTimeout, onError, successTickDurationMs],
	);

	const isBusy = phase === "processing";

	return {
		phase,
		isBusy,
		runLiveSaving,
	};
}

export function isPromiseLike(value: unknown): value is Promise<unknown> {
	return (
		value != null &&
		typeof value === "object" &&
		"then" in value &&
		typeof (value as Promise<unknown>).then === "function"
	);
}
